import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import merge from 'lodash/merge';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { normalize } from 'normalizr';
import { Space as SpaceApi } from 'api/space/space';
import {
  EntityState,
  FetchStatus,
  NormalizedData,
  SavePayloadAction,
} from 'data/core';
import { itemsFetchStatus } from 'data/core/utils';
import { CreateSpacePayload } from './actions';
import { spaceSchema, Space, spaceNormalizer } from './schema';

const INITIAL_DATA: SpaceState = {
  items: {},
  itemsFetchStatus: {},
  loading: false,
  error: null,
  creating: false,
  createError: null,
  isDetailLoading: true,
  currentSpace: null,
  isDetailError: false,
  deleting: false,
  deleteError: null,
  edit: false,
  isEditing: false,
  editError: null,
  isLoading: false,
  isError: false,
};

export interface SpaceState extends EntityState<Space> {
  loading: boolean;
  error: null | string;
  creating: boolean;
  createError: null | string;
  isDetailLoading: boolean;
  isDetailError: boolean;
  currentSpace: number | null;
  deleting: boolean;
  deleteError: string | null;
  edit: boolean;
  isEditing: boolean;
  editError: string | null;
}

export const spacesSlice = createSlice({
  initialState: INITIAL_DATA,
  name: 'spaces',
  reducers: {
    addSpace: (
      state: SpaceState,
      { payload }: PayloadAction<CreateSpacePayload>,
    ): SpaceState => {
      const data = normalize(payload, spaceSchema);

      return merge(
        {},
        { ...state, items: { ...state.items, ...data.entities.spaces } },
      );
    },
    cancelAddSpace: (state: SpaceState): SpaceState => {
      const items = pick(
        state.items,
        Object.keys(state.items).filter((i) => Number(i) > 0),
      ) as NormalizedData<Space>;

      return { ...state, items };
    },
    createStart: (state: SpaceState) => ({ ...state, creating: true }),
    createSuccess: (
      state: SpaceState,
      { payload }: PayloadAction<SpaceApi>,
    ) => {
      const normalized = normalize(payload, spaceSchema);
      const tempState = { ...state };

      tempState.items = omit(state.items, 0);

      return merge({}, tempState, {
        items: { ...normalized.entities.spaces },
        creating: false,
      });
    },
    saveSpaces: (
      state: SpaceState,
      { payload }: SavePayloadAction<SpaceApi[]>,
    ): SpaceState => {
      const { data, fetchStatus } = payload;
      const normalizedData = normalize<Space>(data, [spaceSchema]);
      const {
        entities: { spaces },
        result,
      } = normalizedData;

      if (spaces) {
        return {
          ...state,
          items: spaces,
          itemsFetchStatus: itemsFetchStatus(
            result,
            fetchStatus,
            state.itemsFetchStatus,
          ),
        };
      }

      return state;
    },
    setCurrentSpace: (
      state: SpaceState,
      { payload }: PayloadAction<number | null>,
    ) => ({ ...state, currentSpace: payload }),
    fetchDetailStart: (
      state: SpaceState,
      { payload }: PayloadAction<number>,
    ): SpaceState => ({
      ...state,
      itemsFetchStatus: {
        ...state.itemsFetchStatus,
        [payload]: FetchStatus.Loading,
      },
      isDetailLoading: true,
    }),
    fetchDetailSuccess(
      state: SpaceState,
      { payload }: PayloadAction<SpaceApi>,
    ): SpaceState {
      const {
        entities: { spaces },
      } = spaceNormalizer(payload);

      return merge({}, state, {
        items: { ...state.items, ...spaces },
        itemsFetchStatus: {
          ...state.itemsFetchStatus,
          [payload.spaceId]: FetchStatus.Loaded,
        },
        isDetailLoading: false,
      });
    },
    fetchDetailFailure(state: SpaceState): SpaceState {
      return merge({}, state, {
        isDetailLoading: false,
        isDetailError: true,
      });
    },
    deleteStart(
      state: SpaceState,
      { payload }: PayloadAction<number>,
    ): SpaceState {
      return {
        ...state,
        currentSpace: payload,
        deleting: true,
      };
    },
    deleteSuccess(
      state: SpaceState,
      { payload }: PayloadAction<number>,
    ): SpaceState {
      return {
        ...state,
        items: omit(state.items, payload),
        deleting: false,
        deleteError: null,
        currentSpace: null,
      };
    },
    deleteFailure(
      state: SpaceState,
      { payload }: PayloadAction<string>,
    ): SpaceState {
      return {
        ...state,
        deleting: false,
        deleteError: payload,
        currentSpace: null,
      };
    },
    editSpace(
      state: SpaceState,
      { payload }: PayloadAction<number>,
    ): SpaceState {
      return {
        ...state,
        currentSpace: payload,
        edit: true,
        isEditing: false,
        editError: null,
      };
    },
    cancelEdit(state: SpaceState): SpaceState {
      return {
        ...state,
        currentSpace: null,
        edit: false,
        isEditing: false,
        editError: null,
      };
    },
    editStart(
      state: SpaceState,
      { payload }: PayloadAction<number>,
    ): SpaceState {
      return {
        ...state,
        currentSpace: payload,
        edit: true,
        isEditing: true,
        editError: null,
      };
    },
    editSuccess(
      state: SpaceState,
      { payload }: PayloadAction<SpaceApi>,
    ): SpaceState {
      const normalizedData = normalize(payload, spaceSchema);

      return merge({}, state, {
        items: { ...state.items, ...normalizedData.entities.spaces },
        isEditing: false,
        editError: null,
        currentSpace: null,
      });
    },
    editFailure(
      state: SpaceState,
      { payload }: PayloadAction<string>,
    ): SpaceState {
      return {
        ...state,
        edit: false,
        isEditing: false,
        editError: payload,
        currentSpace: null,
      };
    },
    addSpaceTabsToSpace(
      state: SpaceState,
      { payload }: PayloadAction<{ spaceId: number; spaceTabs: string[] }>,
    ): SpaceState {
      const { spaceId, spaceTabs } = payload;
      const space = state.items[spaceId];

      if (space) {
        return {
          ...state,
          items: {
            ...state.items,
            [spaceId]: {
              ...space,
              spaceTabs: [...space.spaceTabs, ...spaceTabs],
            },
          },
        };
      }

      return state;
    },

    removeSpaceTabFromSpace(
      state: SpaceState,
      { payload }: PayloadAction<{ spaceId: number; spaceTabId: string }>,
    ): SpaceState {
      const { spaceId, spaceTabId } = payload;
      const space = state.items[spaceId];

      if (space) {
        return {
          ...state,
          items: {
            ...state.items,
            [spaceId]: {
              ...space,
              spaceTabs: [...space.spaceTabs.filter((s) => s !== spaceTabId)],
            },
          },
        };
      }

      return state;
    },
  },
});
